/**
 * Assets: uvt-problem.js
 * -----------------------------------------------------------------------------
 * Card interaction for sections/uvt-problem.liquid (Problem and Install).
 * - Selecting a card shows its description and its image, selecting it
 *   again returns to the default image.
 * - data-uvt-static-mobile (Problem): up to 680px the cards are disabled
 *   and every description is shown.
 * - data-uvt-start="first" (Install): the first card starts selected.
 * Re-initialises when the section is re-rendered in the theme editor.
 *
 */
(() => {
  const mobileQuery = window.matchMedia('(max-width: 680px)');

  /**
   * Sets up one problem section.
   * @param {HTMLElement} root - Element with the data-uvt-problem attribute.
   */
  function init(root) {
    if (root.dataset.uvtReady) {
      return;
    }

    root.dataset.uvtReady = 'true';
    const buttons = Array.from(root.querySelectorAll('.uvt-problem-card__action'));
    const images = Array.from(root.querySelectorAll('[data-uvt-image]'));
    const status = root.querySelector('[data-uvt-status]');
    const hasStaticMobile = root.hasAttribute('data-uvt-static-mobile');
    const startButton = root.dataset.uvtStart === 'first' ? buttons[0] || null : null;

    /**
     * Whether the cards are currently a static list.
     * @returns {Boolean}
     */
    function isStatic() {
      return hasStaticMobile && mobileQuery.matches;
    }

    /**
     * Selects a card, or returns to the default state when null.
     * @param {HTMLElement|null} button - Card button to select.
     */
    function activate(button) {
      buttons.forEach((other) => {
        const selected = other === button;
        const description = other.querySelector('.uvt-problem-card__description');

        other.setAttribute('aria-pressed', String(selected));
        other.closest('.uvt-problem-card').classList.toggle('is-selected', selected);

        if (description) {
          description.hidden = isStatic() ? false : !selected;
        }
      });

      const imageKey = button && button.dataset.image ? button.dataset.image : 'default';

      images.forEach((image) => {
        image.hidden = image.dataset.uvtImage !== imageKey;
      });

      if (!status) {
        return;
      }

      const heading = button ? button.querySelector('.uvt-problem-card__heading') : null;

      status.textContent = heading ?
        status.dataset.template.replace('[title]', heading.textContent.trim()) :
        status.dataset.default;
    }

    /**
     * Switches between the interactive and static (mobile) modes, then
     * returns to the starting state.
     */
    function syncMode() {
      buttons.forEach((button) => {
        button.disabled = isStatic();
      });

      activate(isStatic() ? null : startButton);
    }

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        if (isStatic()) {
          return;
        }

        activate(button.getAttribute('aria-pressed') === 'true' ? null : button);
      });
    });

    if (hasStaticMobile) {
      mobileQuery.addEventListener('change', syncMode);
    }

    syncMode();

    // Warm the card images so swapping them is instant, like the design
    window.addEventListener('load', () => {
      images.forEach((image) => {
        image.loading = 'eager';
      });
    }, { once: true });
  }

  /**
   * Sets up every problem section inside a container.
   * @param {ParentNode} container - Document or re-rendered section.
   */
  function initAll(container) {
    container.querySelectorAll('[data-uvt-problem]').forEach(init);
  }

  initAll(document);

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
